import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

export type ParamPatch = Record<string, string | string[] | null | undefined>;

/** 현재 쿼리에 patch를 적용한 새 쿼리. 다중값은 쉼표로 이어 붙이고, 빈 값은 키째 지운다. */
export function applyPatch(params: URLSearchParams, patch: ParamPatch): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const [key, value] of Object.entries(patch)) {
    const serialized = Array.isArray(value) ? value.join(',') : value;
    if (serialized) {
      next.set(key, serialized);
    } else {
      next.delete(key);
    }
  }
  return next;
}

/**
 * 목록 상태(검색·필터·정렬·페이지·열린 상세)를 URL 쿼리에 둔다 — 새로고침·링크 공유·뒤로가기에 살아남는다
 * (Cloudscape "filter persistence"). 다중값은 쉼표로 이어 붙이고, 빈 값은 키째 지운다.
 */
export function useUrlState() {
  const [params, setParams] = useSearchParams();

  const get = useCallback((key: string) => params.get(key) ?? '', [params]);
  const getList = useCallback(
    (key: string) => (params.get(key) ?? '').split(',').filter(Boolean),
    [params],
  );

  /** replace 기본값 true — 타이핑·필터 변경마다 히스토리를 쌓지 않는다. 상세 열기처럼 뒤로가기로 되돌릴 동작만 false. */
  const set = useCallback(
    (patch: ParamPatch, options: { replace?: boolean } = {}) => {
      setParams((prev) => applyPatch(prev, patch), { replace: options.replace ?? true });
    },
    [setParams],
  );

  /** 링크용 — 현재 쿼리에 patch를 적용한 `?...` 문자열(같은 경로 안 이동). */
  const hrefWith = useCallback(
    (patch: ParamPatch) => `?${applyPatch(params, patch).toString()}`,
    [params],
  );

  return { params, get, getList, set, hrefWith };
}
